import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface Group {
  id: string;
  name: string;
  description: string | null;
  isGlobal: boolean;
  createdAt: string;
}

interface GroupPost {
  id: string;
  groupId: string;
  authorId: string;
  authorName: string;
  title: string;
  body: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [posts, setPosts] = useState<GroupPost[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [error, setError] = useState('');

  // New post form
  const [postTitle, setPostTitle] = useState('');
  const [postBody, setPostBody] = useState('');
  const [postPinned, setPostPinned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [postError, setPostError] = useState('');

  // Create group form
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupGlobal, setNewGroupGlobal] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    setError('');
    try {
      const json = await apiFetch<Group[]>('/api/admin/groups');
      if (!json.success) { setError(json.error ?? 'Failed to load groups'); return; }
      const groupList = json.data ?? [];
      setGroups(groupList);
      // Auto-select global group if none selected
      if (!selectedGroup && groupList.length > 0) {
        const global = groupList.find((g) => g.isGlobal) ?? groupList[0];
        setSelectedGroup(global);
      }
    } catch {
      setError('Failed to load groups');
    } finally {
      setLoadingGroups(false);
    }
  }, [selectedGroup]);

  const loadPosts = useCallback(async (groupId: string) => {
    setLoadingPosts(true);
    try {
      const json = await apiFetch<{ group: Group; posts: GroupPost[] }>(`/api/admin/groups/${groupId}/posts`);
      if (json.success) setPosts(json.data?.posts ?? []);
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedGroup) loadPosts(selectedGroup.id);
  }, [selectedGroup, loadPosts]);

  const handleSelectGroup = (group: Group) => {
    setSelectedGroup(group);
    setPosts([]);
    setPostError('');
  };

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup) return;
    if (!postTitle.trim() || !postBody.trim()) { setPostError('Title and body are required'); return; }
    setSubmitting(true);
    setPostError('');
    try {
      const json = await apiFetch(`/api/admin/groups/${selectedGroup.id}/posts`, {
        method: 'POST',
        body: JSON.stringify({ title: postTitle.trim(), body: postBody.trim(), pinned: postPinned }),
      });
      if (!json.success) { setPostError(json.error ?? 'Failed to create post'); return; }
      setPostTitle('');
      setPostBody('');
      setPostPinned(false);
      await loadPosts(selectedGroup.id);
    } catch {
      setPostError('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePin = async (post: GroupPost) => {
    if (!selectedGroup) return;
    try {
      await apiFetch(`/api/admin/groups/${selectedGroup.id}/posts/${post.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ pinned: !post.pinned }),
      });
      await loadPosts(selectedGroup.id);
    } catch {
      /* noop */
    }
  };

  const handleDeletePost = async (post: GroupPost) => {
    if (!selectedGroup) return;
    if (!confirm(`Delete "${post.title}"?`)) return;
    try {
      await apiFetch(`/api/admin/groups/${selectedGroup.id}/posts/${post.id}`, { method: 'DELETE' });
      await loadPosts(selectedGroup.id);
    } catch {
      /* noop */
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const json = await apiFetch('/api/admin/groups', {
        method: 'POST',
        body: JSON.stringify({ name: newGroupName.trim(), description: newGroupDesc.trim() || undefined, is_global: newGroupGlobal }),
      });
      if (json.success) {
        setNewGroupName('');
        setNewGroupDesc('');
        setNewGroupGlobal(false);
        setShowCreateGroup(false);
        await loadGroups();
      }
    } finally {
      setCreatingGroup(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Groups</h1>
          <p style={styles.subtitle}>Manage groups and post announcements visible to all members.</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowCreateGroup(!showCreateGroup)}>
          {showCreateGroup ? 'Cancel' : '+ New Group'}
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {showCreateGroup && (
        <form onSubmit={handleCreateGroup} style={styles.createGroupForm}>
          <h3 style={{ color: COLORS.textPrimary, margin: '0 0 12px' }}>Create Group</h3>
          <div style={styles.formRow}>
            <input
              style={styles.input}
              placeholder="Group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              required
            />
            <input
              style={styles.input}
              placeholder="Description (optional)"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
            />
            <label style={styles.checkLabel}>
              <input type="checkbox" checked={newGroupGlobal} onChange={(e) => setNewGroupGlobal(e.target.checked)} />
              <span style={{ color: COLORS.textSecondary, marginLeft: 6 }}>Global (all members)</span>
            </label>
            <button type="submit" style={styles.btnPrimary} disabled={creatingGroup}>
              {creatingGroup ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <div style={styles.layout}>
        {/* Sidebar: group list */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>Groups</div>
          {loadingGroups ? (
            <div style={styles.muted}>Loading…</div>
          ) : groups.length === 0 ? (
            <div style={styles.muted}>No groups yet.</div>
          ) : (
            groups.map((g) => (
              <button
                key={g.id}
                style={{
                  ...styles.groupItem,
                  ...(selectedGroup?.id === g.id ? styles.groupItemActive : {}),
                }}
                onClick={() => handleSelectGroup(g)}
              >
                <span style={styles.groupName}>{g.name}</span>
                {g.isGlobal && <span style={styles.globalBadge}>Global</span>}
              </button>
            ))
          )}
        </div>

        {/* Main: posts */}
        <div style={styles.main}>
          {!selectedGroup ? (
            <div style={styles.muted}>Select a group to manage posts.</div>
          ) : (
            <>
              <div style={styles.postsHeader}>
                <div>
                  <h2 style={styles.groupTitle}>{selectedGroup.name}</h2>
                  {selectedGroup.description && (
                    <p style={styles.groupDesc}>{selectedGroup.description}</p>
                  )}
                </div>
              </div>

              {/* New post form */}
              <form onSubmit={handleCreatePost} style={styles.postForm}>
                <h3 style={{ color: COLORS.textPrimary, margin: '0 0 12px' }}>New Post</h3>
                <input
                  style={styles.input}
                  placeholder="Title"
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                  required
                />
                <textarea
                  style={{ ...styles.input, height: 100, resize: 'vertical' }}
                  placeholder="Body — write your announcement here…"
                  value={postBody}
                  onChange={(e) => setPostBody(e.target.value)}
                  required
                />
                <div style={styles.formRow}>
                  <label style={styles.checkLabel}>
                    <input type="checkbox" checked={postPinned} onChange={(e) => setPostPinned(e.target.checked)} />
                    <span style={{ color: COLORS.textSecondary, marginLeft: 6 }}>Pin this post</span>
                  </label>
                  <button type="submit" style={styles.btnPrimary} disabled={submitting}>
                    {submitting ? 'Posting…' : 'Post'}
                  </button>
                </div>
                {postError && <div style={styles.errorText}>{postError}</div>}
              </form>

              {/* Posts list */}
              {loadingPosts ? (
                <div style={styles.muted}>Loading posts…</div>
              ) : posts.length === 0 ? (
                <div style={styles.muted}>No posts yet. Create the first one above.</div>
              ) : (
                <div style={styles.postsList}>
                  {posts.map((post) => (
                    <div
                      key={post.id}
                      style={{
                        ...styles.postCard,
                        ...(post.pinned ? styles.postCardPinned : {}),
                      }}
                    >
                      <div style={styles.postCardHeader}>
                        <div>
                          {post.pinned && <span style={styles.pinTag}>📌 Pinned</span>}
                          <h3 style={styles.postTitle}>{post.title}</h3>
                          <div style={styles.postMeta}>
                            {post.authorName} · {formatDate(post.createdAt)}
                          </div>
                        </div>
                        <div style={styles.postActions}>
                          <button
                            style={post.pinned ? styles.btnSecondary : styles.btnOutline}
                            onClick={() => handleTogglePin(post)}
                            title={post.pinned ? 'Unpin' : 'Pin'}
                          >
                            {post.pinned ? 'Unpin' : 'Pin'}
                          </button>
                          <button
                            style={styles.btnDanger}
                            onClick={() => handleDeletePost(post)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <p style={styles.postBody}>{post.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: COLORS.textPrimary, fontSize: 28, fontWeight: 700, margin: '0 0 4px' },
  subtitle: { color: COLORS.textMuted, fontSize: 14, margin: 0 },
  errorBanner: { backgroundColor: '#5c1a1a', color: '#ff8080', border: '1px solid #8b1a1a', borderRadius: 8, padding: '10px 16px', marginBottom: 20 },
  errorText: { color: '#ff8080', fontSize: 13, marginTop: 8 },
  muted: { color: COLORS.textMuted, fontSize: 14, padding: 16 },
  layout: { display: 'flex', gap: 24, alignItems: 'flex-start' },
  sidebar: { width: 220, backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 8, flexShrink: 0 },
  sidebarHeader: { color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, padding: '4px 8px 8px' },
  groupItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 12px', borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: COLORS.textSecondary, fontSize: 14 },
  groupItemActive: { backgroundColor: COLORS.primaryGreen, color: COLORS.brassLight },
  groupName: { fontWeight: 600 },
  globalBadge: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, backgroundColor: 'rgba(255,215,0,0.15)', color: COLORS.textGold, borderRadius: 4, padding: '2px 6px' },
  main: { flex: 1 },
  postsHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 },
  groupTitle: { color: COLORS.textPrimary, fontSize: 20, fontWeight: 700, margin: '0 0 4px' },
  groupDesc: { color: COLORS.textMuted, fontSize: 13, margin: 0 },
  postForm: { backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 20, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 12 },
  formRow: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' as const },
  input: { backgroundColor: COLORS.backgroundSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, padding: '10px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box' as const },
  checkLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 },
  createGroupForm: { backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 20, marginBottom: 24 },
  postsList: { display: 'flex', flexDirection: 'column', gap: 16 },
  postCard: { backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 20 },
  postCardPinned: { borderColor: COLORS.brass, borderLeftWidth: 4 },
  postCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 },
  postTitle: { color: COLORS.textPrimary, fontSize: 16, fontWeight: 700, margin: '4px 0 4px' },
  postMeta: { color: COLORS.textMuted, fontSize: 12 },
  pinTag: { fontSize: 11, color: COLORS.textGold, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5, display: 'block', marginBottom: 2 },
  postBody: { color: COLORS.textSecondary, fontSize: 14, margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' as const },
  postActions: { display: 'flex', gap: 8, flexShrink: 0 },
  btnPrimary: { backgroundColor: COLORS.primaryGreen, color: COLORS.brassLight, border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { backgroundColor: COLORS.brass, color: '#111', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnOutline: { backgroundColor: 'transparent', color: COLORS.textMuted, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
  btnDanger: { backgroundColor: '#5c1a1a', color: '#ff8080', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
};
